import Game from '../../../Game';
import BasePopupHandler from '../Menu/BasePopupHandler';
import GameObject from '../../../GameObject/GameObject';
import BaseUISystem from '../BaseUISystem';
import { isShopSupported } from '../../../Utility/Platform';
import Element from '../../../Prototype/UI/Element';
import { UIActions } from '../../../State/UI/UIActions';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { GameActions } from '../../../State/Game/GameActions';
import { AD_COINS_REWARD, AD_COOLDOWN_TIME, ADS_BEFORE_COOLDOWN, AdsConfig } from '../../../Constants/Ads';
import { AdvertisementArea } from '../../../Utility/SDK/Advertisement';
import CrossPromoDriver, { CrossPromoPlacement } from '../../../Utility/CrossPromo/CrossPromoDriver';
import CrossPromoCampaign from '../../../Utility/CrossPromo/CrossPromoCampaign';
import UITextComponent from '../../../Component/UI/Text';
import Renderable from '../../../Component/Renderable';
import DateHelper from '../../../Utility/DateHelper';
import Timer from '../../Timer/Timers/Timer';
import { TimerType } from '../../Timer/TimerTypes';
import { ContextType } from '../../../State/Game/ContextType';
import { IGameState } from '../../../State/Game/IGameState';
import { PopupID } from '../../../State/UI/PopupID';
import { SHOP_UNLOCK_LEVEL } from 'Constants/Shop';

type NoCoinsMenuHandlerProps = {
    onClose?: () => void;
}

export default class NoCoinsMenuHandler extends BasePopupHandler {
    protected adTimer: Timer;
    private campaign: CrossPromoCampaign;
    public shopEnabled: boolean;
    public placement: AdvertisementArea;
    private onCloseCallback?: () => void;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.NO_COINS_MENU, uiSystem);
    }

    protected createPopup(params: any = {}): void {
        try {
            const state: IGameState = Game.store.getState();
            this.placement = params.placement;
            this.shopEnabled =
                isShopSupported() &&
                Game.player.getHighestLevelCompleted() >= SHOP_UNLOCK_LEVEL;

            this.underlayParams.cacheKey = 'shop_banner';
            this.underlayParams.layout.anchors = [0.5, 0.47, 0.5, 0.47];

            // Set button text
            this.submitBtnParams.text.textLocalizationKey = this.shopEnabled
                ? 'GoToShop'
                : 'Watch';
            // Set style
            const buttonStyle = Game.style.getStyle('popup.green_btn');
            this.submitBtnParams.text.style = this.shopEnabled
                ? buttonStyle
                : { ...buttonStyle, stroke: '#8c0154' };
            // Choose button asset
            this.submitBtnParams.button.cacheKey = this.shopEnabled
                ? 'button_medium_green'
                : 'button_medium_purple';
            // Set bounds
            this.submitBtnParams.text.bounds = this.shopEnabled
                ? [220, 110]
                : this.submitBtnParams.text.bounds;

            this.submitBtnParams.tintOnDisable = !this.shopEnabled;

            super.createPopup({
                buttons: [this.closeBtnParams, this.submitBtnParams],
                underlay: this.underlayParams,
                title: 'NotEnoughCoins',
            });

            const shopText: Element = GameObject.Instantiate(Element, {
                name: 'info_text',
                layout: {
                    anchors: [0.5, 0.77, 0.5, 0.77],
                    pivot: [0.5, 0.5],
                    offsets: [0, 0, 0, 0],
                    scale: [1, 1],
                },
                components: [
                    {
                        type: 'text',
                        data: {
                            style: {
                                font: 'FrancoisOne',
                                fill: '#bb3c2f',
                                fontSize: '30px',
                                align: 'center',
                            },
                            textLocalizationKey: this.shopEnabled
                                ? 'FantasticPackages'
                                : 'WatchVideo',
                            bounds: [600, 140],
                            align: 'center',
                        },
                    },
                ],
            });
            this.background.addChild(shopText);

            if (!this.shopEnabled) {
                const adIcon: Element = GameObject.Instantiate(Element, {
                    name: 'ad_icon',
                    layout: {
                        anchors: [0.83, 0.13, 0.83, 0.13],
                        pivot: [0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'ad_icon_2',
                            },
                        },
                    ],
                });

                this.submitBtn.button.addChild(adIcon);

                if (isShopSupported(state)) {
                    this.adTimer = new Timer();
                    const remainingAdCount: number = state.playerState.progress
                        .adsWatched
                        ? ADS_BEFORE_COOLDOWN -
                          state.playerState.progress.adsWatched
                        : ADS_BEFORE_COOLDOWN;
                    const remainingAds: Element = GameObject.Instantiate(
                        Element,
                        {
                            name: 'remaining_ads',
                            layout: {
                                anchors: [0.5, 0.81, 0.5, 0.81],
                                pivot: [0.5, 0.5],
                                offsets: [0, 0, 0, 0],
                            },
                            components: [
                                {
                                    type: 'text',
                                    data: {
                                        text: `${remainingAdCount}x ${Game.locale.getText(
                                            'Available'
                                        )}`,
                                        style: Game.style.getStyle(
                                            'ads.remainingAds'
                                        ),
                                    },
                                },
                            ],
                        }
                    );
                    this.submitBtn.button.addChild(remainingAds);
                    this.adTimer = new Timer(false);
                    this.adTimer.onUpdate = this.updateAdButton.bind(this);
                }
            }

            // Add character icon
            const character: Element = GameObject.Instantiate(Element, {
                name: 'character',
                layout: {
                    anchors: [0, 0.5, 0, 0.5],
                    pivot: [0.5, 0.5],
                    offsets: [40, -80, 40, -80],
                },
                components: [
                    {
                        type: 'image',
                        data: {
                            cacheKey: 'character_idle_1',
                        },
                    },
                ],
            });
            this.background.addChild(character);
        } catch (e) {
            console.log(e);
        }
    }

    public onShow(props?: NoCoinsMenuHandlerProps): void {
        this.onCloseCallback = props.onClose;

        const state: IGameState = Game.store.getState();

        CrossPromoDriver.getCampaignOfType(CrossPromoPlacement.NO_COINS, state)
            .catch(() => {
                super.onClose();
            })
            .then((campaign: CrossPromoCampaign) => {
                this.campaign = campaign;
            });
        
        if (this.adTimer) {
            this.updateAdButton(state);
            this.adTimer.start();
        }
    }
    public onShown(params?: any) {
        super.onShown(params);
        if (!this.shopEnabled) {
            this.updateAdButton();
        }
    }
    protected onSubmit(): void {
        super.onSubmit();
        if (this.shopEnabled) {
            UIActions.showPopup(PopupID.SHOP_MENU, {
                xPromoCampaign: this.campaign,
            });
            try {
                Game.sdk.gameTracking(FBTrackingEvent.OpenShop_NotEnoughCoins);
            } catch (e) {
                console.error('Failed to track', e);
            }
        } else {
            GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
                placement: AdsConfig.getRewardedNoCoins(Game.config),
                payload: {
                    g_gameArea:
                        this.placement || AdvertisementArea.NO_COINS_MENU,
                    reward: AD_COINS_REWARD,
                },
            });
        }
    }
    protected onClose(): void {
        super.onClose();
        if (this.campaign !== undefined) {
            UIActions.showPopup(PopupID.CROSS_PROMO_MENU, this.campaign);
        }
    }
    public onDestroy(): void {
        if (this.adTimer) {
            this.adTimer.stop();
        }
        super.onDestroy();
    }
    public onHide() {
        if (this.adTimer) {
            this.adTimer.stop();
        }
        this.onCloseCallback?.();
    }
    protected updateAdButton(state: IGameState = Game.store.getState()): void {
        try {
            const time: Date = state.playerState.timers.has(
                TimerType.AD_COOLDOWN
            )
                ? state.playerState.timers.get(TimerType.AD_COOLDOWN)
                : null;
            const remainingAdCount: number = state.playerState.progress
                .adsWatched
                ? ADS_BEFORE_COOLDOWN - state.playerState.progress.adsWatched
                : ADS_BEFORE_COOLDOWN;

            const adsRemainingText: GameObject = this.submitBtn.findChildByName(
                'remaining_ads'
            );
            if (adsRemainingText && !adsRemainingText.destroyed) {
                adsRemainingText.getComponent(
                    UITextComponent
                ).text = `${remainingAdCount}x ${Game.locale.getText(
                    'Available'
                )}`;
                adsRemainingText.getComponent<Renderable>(Renderable).visible =
                    time === null;
            }
            if (this.submitBtn && !this.submitBtn.destroyed) {
                const completeTime: Date =
                    time === null
                        ? null
                        : new Date(time.getTime() + AD_COOLDOWN_TIME);
                if (this.submitBtn.text) {
                    this.submitBtn.text.getComponent(UITextComponent).text =
                        time === null
                            ? Game.locale.getText('Watch')
                            : DateHelper.timeTo(
                                  DateHelper.now,
                                  completeTime,
                                  'hh:mm:ss'
                              );
                }
                this.submitBtn.enabled = time === null;
            }
        } catch (e) {
            console.warn('Failed to update ad button', e);
        }
    }
    public async preload(): Promise<void> {}
}
